import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Archive, Trash2, Edit2, ArchiveRestore, Calendar, Key, LogIn } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { Patient } from '../../../shared/professional-types';
import CreatePatientModal from '../components/professional/CreatePatientModal';
import UpdatePatientModal from '../components/professional/UpdatePatientModal';
import ChangePasswordModal from '../components/professional/ChangePasswordModal';

export default function ProfessionalPatientsPage() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadPatients();
  }, [showArchived]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/professional/patients', {
        params: { archived: showArchived },
      });
      if (response.data.ok) {
        setPatients(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArchive = async (patient: Patient) => {
    const confirmMessage = patient.activo
      ? '¿Archivar este paciente?'
      : '¿Reactivar este paciente?';

    if (!confirm(confirmMessage)) return;

    try {
      const response = await api.patch(`/professional/patients/${patient.id}/archive`, {
        activo: !patient.activo,
      });

      if (response.data.ok) {
        loadPatients();
      }
    } catch (error) {
      console.error('Error al archivar/activar paciente:', error);
      alert('Error al modificar el paciente');
    }
  };

  const handleDelete = async (patient: Patient) => {
    if (!confirm(`¿Eliminar permanentemente a ${patientLabel(patient)}?\n\nEsta acción NO se puede deshacer.`)) {
      return;
    }

    try {
      const response = await api.delete(`/professional/patients/${patient.id}`);
      if (response.data.ok) {
        loadPatients();
      }
    } catch (error) {
      console.error('Error al eliminar paciente:', error);
      alert('Error al eliminar el paciente');
    }
  };

  const patientLabel = (patient: Patient): string => {
    if (patient.identificador) return patient.identificador;
    if (patient.first_name || patient.last_name)
      return `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
    return patient.username || `Paciente #${patient.id}`;
  };

  const handleImpersonate = async (patient: Patient) => {
    try {
      const res = await api.post(`/professional/impersonate/${patient.usuario_id}`);
      if (res.data.ok) {
        setUser(res.data.data.user);
        navigate('/juegos');
      }
    } catch (err) {
      console.error('Error al impersonar paciente:', err);
      alert('No se pudo acceder como este paciente');
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-4xl font-display font-bold mb-2">
              Mis Pacientes
            </h1>
            <p className="text-white/70">
              {patients.length} {patients.length === 1 ? 'paciente' : 'pacientes'}
              {showArchived ? ' archivados' : ' activos'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="btn-neureka-secondary"
            >
              {showArchived ? (
                <>
                  <ArchiveRestore className="w-4 h-4" />
                  Ver Activos
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4" />
                  Ver Archivados
                </>
              )}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-neureka"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo Paciente
            </button>
          </div>
        </motion.div>

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
            <p className="mt-4 text-white/70">Cargando pacientes...</p>
          </div>
        ) : patients.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-container text-center py-12"
          >
            <UserPlus className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {showArchived ? 'No hay pacientes archivados' : 'No tienes pacientes aún'}
            </h3>
            <p className="text-white/60 mb-6">
              {showArchived
                ? 'Los pacientes archivados aparecerán aquí'
                : 'Crea tu primer paciente para comenzar'}
            </p>
            {!showArchived && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-neureka"
              >
                <UserPlus className="w-4 h-4" />
                Crear Primer Paciente
              </button>
            )}
          </motion.div>
        ) : (
          /* Patients Table */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-container overflow-x-auto"
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/70 font-medium">Paciente</th>
                  <th className="text-left p-4 text-white/70 font-medium">Diagnóstico</th>
                  <th className="text-center p-4 text-white/70 font-medium">Partidas</th>
                  <th className="text-center p-4 text-white/70 font-medium">Sesiones</th>
                  <th className="text-center p-4 text-white/70 font-medium">Fecha Alta</th>
                  <th className="text-center p-4 text-white/70 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient, index) => (
                  <motion.tr
                    key={patient.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-purple-300 font-semibold text-sm">
                            {patientLabel(patient).slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold">
                            {patientLabel(patient)}
                          </div>
                          <div className="text-sm text-white/50">
                            @{patient.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white/70">
                        {patient.diagnostico || '-'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-semibold text-green-400">
                        {patient.total_partidas || 0}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold">
                          {patient.total_sesiones || 0}
                        </span>
                        <span className="text-xs text-white/50">
                          {patient.sesiones_finalizadas || 0} finalizadas
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2 text-white/70">
                        <Calendar className="w-4 h-4" />
                        {formatDate(patient.fecha_asignacion)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleImpersonate(patient)}
                          className="p-2 rounded-lg hover:bg-amber-500/20 text-amber-400 transition-colors"
                          title="Entrar como este paciente"
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPatient(patient);
                            setShowUpdateModal(true);
                          }}
                          className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400 transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPatient(patient);
                            setShowPasswordModal(true);
                          }}
                          className="p-2 rounded-lg hover:bg-purple-500/20 text-purple-400 transition-colors"
                          title="Cambiar Contraseña"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleArchive(patient)}
                          className={`p-2 rounded-lg transition-colors ${
                            patient.activo
                              ? 'hover:bg-yellow-500/20 text-yellow-400'
                              : 'hover:bg-green-500/20 text-green-400'
                          }`}
                          title={patient.activo ? 'Archivar' : 'Reactivar'}
                        >
                          {patient.activo ? (
                            <Archive className="w-4 h-4" />
                          ) : (
                            <ArchiveRestore className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(patient)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}

        {/* Modales */}
        <CreatePatientModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPatientCreated={(patient) => {
            loadPatients();
          }}
        />

        <UpdatePatientModal
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedPatient(null);
          }}
          patient={selectedPatient}
          onPatientUpdated={() => {
            loadPatients();
          }}
        />

        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedPatient(null);
          }}
          patient={selectedPatient}
          onPasswordChanged={() => {
            setShowPasswordModal(false);
            setSelectedPatient(null);
          }}
        />
      </div>
    </div>
  );
}
